/**
 * Smart Extend — trigger + modal (spec:
 * docs/superpowers/specs/2026-08-13-smart-extend-design.md).
 *
 * The RESTING Cut & Extend card shows only a one-line trigger; everything
 * else lives in a design-system modal (SceneDetailModal's exact pattern:
 * body portal, blurred fixed overlay, 3-row grid panel, Escape + backdrop
 * close). The trigger probes availability eagerly and renders NOTHING
 * unless the server says the clip is extendable — flag off, upload-mode,
 * no cached transcript, or at the cap keep the card pixel-identical to
 * the pre-feature UI.
 *
 * The modal is self-contained: pick a suggestion → a debounced dry-run
 * (trim-preview WITH the smart fields, so the server verifies the pick and
 * the copy is the real plan) → Apply re-cuts and reloads, same lifecycle
 * as the panel's own Apply.
 */
import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Loader2, Sparkles, X } from "lucide-react";
import {
  applyTrim,
  extendContext,
  extendResolve,
  extendSuggest,
  HttpError,
  openingSuggest,
  trimPreview,
  type ExtendContext,
  type ExtendSuggestion,
  type OpeningSuggestion,
  type TrimPreview,
} from "../api";
import { useIsMobile } from "../lib/useIsMobile";
import { ctaSmallStyle } from "./styles";

const round1 = (n: number): number => Math.round(n * 10) / 10;

function friendlyError(e: unknown): string {
  if (e instanceof HttpError && e.status === 502) return "the AI call failed — try again";
  if (e instanceof HttpError && e.status === 409) return "this clip is busy re-rendering — wait for it to finish";
  return e instanceof Error ? e.message : String(e);
}

function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--ql-fg-subtle)",
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

const transcriptBoxStyle: CSSProperties = {
  background: "rgba(255,255,255,.03)",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 12,
  padding: "12px 14px",
  fontSize: 13,
  lineHeight: 1.75,
  overflowWrap: "anywhere",
  maxHeight: 240,
  overflowY: "auto",
  color: "var(--ql-fg)",
};

const pillStyle = (active: boolean, disabled: boolean): CSSProperties => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "10px 12px",
  borderRadius: 12,
  border: active ? "1px solid rgba(255,213,0,.45)" : "1px solid rgba(255,255,255,.10)",
  background: active ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.03)",
  color: "var(--ql-fg)",
  fontSize: 12.5,
  lineHeight: 1.55,
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.55 : 1,
  fontFamily: "inherit",
});

/** How many words after (or before) the clicked one form the anchor phrase.
 *  Long enough that the server can usually resolve it without growing; the
 *  uniqueness growth there is what actually guarantees a correct snap. */
const CLICK_PHRASE_WORDS = 8;

/**
 * Transcript rendered as individually clickable words.
 *
 * Word times are NOT computed here: the panel shows de-duplicated caption text,
 * so any browser-side interpolation would drift from the real transcript. The
 * click only produces a PHRASE; the server locates and verifies it.
 * `direction: "start"` takes the clicked word plus the following ones (the clip
 * begins ON it); `"end"` takes the preceding ones plus the clicked word (the
 * clip ends AFTER it).
 */
function ClickableTranscript({
  lines,
  direction,
  isEligible,
  isAdded,
  disabled,
  onPick,
  onBlocked,
}: {
  lines: Array<{ startSec: number; endSec: number; text: string }>;
  direction: "start" | "end";
  isEligible: (l: { startSec: number; endSec: number }) => boolean;
  isAdded: (l: { startSec: number; endSec: number }) => boolean;
  disabled: boolean;
  onPick: (phrase: string) => void;
  /** A click on unreachable text must SAY why — silence reads as a broken
   *  panel (the operator clicked grayed words twice before asking). */
  onBlocked: (line: { startSec: number; endSec: number }) => void;
}): React.JSX.Element {
  const flat: Array<{ word: string; lineIdx: number }> = [];
  lines.forEach((l, lineIdx) => {
    for (const w of l.text.split(/\s+/).filter(Boolean)) flat.push({ word: w, lineIdx });
  });

  return (
    <div style={transcriptBoxStyle} dir="auto" tabIndex={0}>
      {flat.map((tok, i) => {
        const line = lines[tok.lineIdx]!;
        const eligible = isEligible(line);
        const added = isAdded(line);
        const prevLine = i > 0 ? lines[flat[i - 1]!.lineIdx]! : null;
        // Mark where the reachable zone begins (start panel) or ends (end
        // panel), so the grayed text reads as "out of budget", not "broken".
        const boundary =
          prevLine !== null && isEligible(prevLine) !== eligible
            ? direction === "start"
              ? "earliest you can start — the cap stops here"
              : "latest you can end — the cap stops here"
            : null;
        const phrase =
          direction === "start"
            ? flat.slice(i, i + CLICK_PHRASE_WORDS).map((t) => t.word).join(" ")
            : flat.slice(Math.max(0, i - CLICK_PHRASE_WORDS + 1), i + 1).map((t) => t.word).join(" ");
        return (
          <Fragment key={`w${i}`}>
            {boundary !== null && (
              <span
                style={{
                  display: "block",
                  margin: "8px 0",
                  fontSize: 10.5,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--ql-fg-subtle)",
                  borderTop: "1px dashed rgba(255,255,255,.16)",
                  paddingTop: 6,
                }}
              >
                ↕ {boundary}
              </span>
            )}
          <span
            role={eligible && !disabled ? "button" : undefined}
            tabIndex={eligible && !disabled ? 0 : undefined}
            title={
              !eligible
                ? "beyond the cap — pick a word closer to the clip"
                : disabled
                  ? undefined
                  : direction === "start"
                    ? "start the clip here"
                    : "end the clip here"
            }
            onClick={() => {
              if (disabled) return;
              if (eligible) onPick(phrase);
              else onBlocked(line);
            }}
            onKeyDown={(e) => {
              if ((e.key !== "Enter" && e.key !== " ") || disabled) return;
              e.preventDefault();
              if (eligible) onPick(phrase);
              else onBlocked(line);
            }}
            className={eligible && !disabled ? "ql-pickword" : undefined}
            style={{
              opacity: eligible ? 1 : 0.4,
              background: added ? "rgba(255,213,0,.10)" : undefined,
              borderRadius: added ? 4 : undefined,
              cursor: disabled ? "default" : eligible ? "pointer" : "not-allowed",
            }}
          >
            {tok.word}{" "}
          </span>
          </Fragment>
        );
      })}
      {lines.length === 0 && (
        <span style={{ color: "var(--ql-fg-muted)" }}>transcript ends at the current cut.</span>
      )}
    </div>
  );
}

/** The one-line affordance on the resting card. Absent unless extendable. */
export function SmartExtendTrigger({ jobId, busy, onJobStarted }: {
  jobId: string;
  busy: boolean;
  /** Apply started a re-cut. A host that follows jobs (the Studio) takes it
   *  from here; absent ⇒ the page reloads, as it always has. */
  onJobStarted?: () => void;
}): React.JSX.Element | null {
  const [ctx, setCtx] = useState<ExtendContext | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCtx(null);
    extendContext(jobId)
      .then((c) => {
        if (!cancelled) setCtx(c);
      })
      .catch(() => {
        /* probe failure ⇒ row stays absent; card is byte-identical */
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (ctx === null || !ctx.available) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={busy}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 10,
          border: "1px solid rgba(255,213,0,.25)",
          background: "rgba(255,213,0,.06)",
          color: "var(--ql-yellow, #FFD500)",
          fontSize: 12,
          fontWeight: 600,
          cursor: busy ? "default" : "pointer",
          fontFamily: "inherit",
          opacity: busy ? 0.55 : 1,
        }}
      >
        <Sparkles size={12} strokeWidth={2} />
        Smart extend
        <span style={{ color: "var(--ql-fg-subtle)", fontWeight: 400 }}>
          {(ctx.backHeadroomSec ?? 0) >= 2
            ? `· ~${Math.round(ctx.headroomSec ?? 0)}s to spend, either end`
            : `· ~${Math.round(ctx.headroomSec ?? 0)}s available`}
        </span>
      </button>
      {open && <SmartExtendModal jobId={jobId} ctx={ctx} onClose={() => setOpen(false)} onJobStarted={onJobStarted} />}
    </>
  );
}

function SmartExtendModal({
  jobId,
  ctx,
  onClose,
  onJobStarted,
}: {
  jobId: string;
  ctx: ExtendContext;
  onClose: () => void;
  onJobStarted?: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [suggestions, setSuggestions] = useState<ExtendSuggestion[] | null>(null);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [picked, setPicked] = useState<ExtendSuggestion | null>(null);
  const [openings, setOpenings] = useState<OpeningSuggestion[] | null>(null);
  const [openingBusy, setOpeningBusy] = useState(false);
  const [openingNote, setOpeningNote] = useState<string | null>(null);
  const [pickedStart, setPickedStart] = useState<OpeningSuggestion | null>(null);
  const [resolvingStart, setResolvingStart] = useState(false);
  const [resolvingEnd, setResolvingEnd] = useState(false);
  const [preview, setPreview] = useState<TrimPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Escape closes (unless mid-apply — the re-cut is already queued then).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !applying) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, applying]);

  // Dry-run the picked suggestion through the REAL planner (smart fields
  // included), so the operator reads the verified plan — never guesswork.
  const reqSeq = useRef(0);
  useEffect(() => {
    setApplyError(null);
    if (picked === null && pickedStart === null) {
      reqSeq.current++;
      setPreview(null);
      setPreviewError(null);
      setPreviewPending(false);
      return;
    }
    setPreviewPending(true);
    const seq = ++reqSeq.current;
    // Both edges ride ONE dry-run, so the combined length is what the server
    // caps — arming a start and an end that individually fit but together
    // exceed the cap surfaces here as a real 400, not at Apply.
    trimPreview(jobId, pickedStart === null ? 0 : -round1(pickedStart.addedSec), picked === null ? 0 : round1(picked.addedSec), {
      ...(picked !== null
        ? { extendEndPhrase: picked.endPhrase, extendEndsAtSourceSec: picked.endsAtSourceSec }
        : {}),
      ...(pickedStart !== null
        ? { extendStartPhrase: pickedStart.startPhrase, extendStartsAtSourceSec: pickedStart.startsAtSourceSec }
        : {}),
    })
      .then((p) => {
        if (reqSeq.current !== seq) return;
        setPreview(p);
        setPreviewError(null);
        setPreviewPending(false);
      })
      .catch((err) => {
        if (reqSeq.current !== seq) return;
        setPreview(null);
        setPreviewError(friendlyError(err));
        setPreviewPending(false);
      });
  }, [jobId, picked, pickedStart]);

  const suggest = (): void => {
    if (suggestBusy || applying) return;
    setSuggestBusy(true);
    setNote(null);
    extendSuggest(jobId)
      .then((r) => {
        // Re-rolling replaces the pills; an armed pick whose pill no longer
        // exists would look unarmed yet still ride Apply. Keep it only when
        // the new list still offers the same anchor.
        setPicked((cur) =>
          cur === null
            ? null
            : (r.suggestions.find((n) => Math.abs(n.endsAtSourceSec - cur.endsAtSourceSec) < 0.05) ?? null),
        );
        setSuggestions(r.suggestions);
        if (r.suggestions.length === 0) {
          setNote(r.warnings[0] ?? "no better ending found in the available headroom");
        }
      })
      .catch((e) => setNote(friendlyError(e)))
      .finally(() => setSuggestBusy(false));
  };

  const suggestOpening = (): void => {
    if (openingBusy || applying) return;
    setOpeningBusy(true);
    setOpeningNote(null);
    openingSuggest(jobId)
      .then((r) => {
        setPickedStart((cur) =>
          cur === null
            ? null
            : (r.suggestions.find((n) => Math.abs(n.startsAtSourceSec - cur.startsAtSourceSec) < 0.05) ?? null),
        );
        setOpenings(r.suggestions);
        if (r.suggestions.length === 0) {
          setOpeningNote(r.warnings[0] ?? "no better opening found in the available lead-in");
        }
      })
      .catch((e) => setOpeningNote(friendlyError(e)))
      .finally(() => setOpeningBusy(false));
  };

  // A clicked word rides the SAME verification the AI picks ride; a refusal is
  // shown in that panel's note line instead of arming something unappliable.
  const pickFromTranscript = (direction: "start" | "end", phrase: string): void => {
    if (applying) return;
    const setNote_ = direction === "start" ? setOpeningNote : setNote;
    setNote_(null);
    if (direction === "start") setResolvingStart(true);
    else setResolvingEnd(true);
    extendResolve<OpeningSuggestion | ExtendSuggestion>(jobId, direction, phrase)
      .then((pick) => {
        if (direction === "start") {
          const p2 = pick as OpeningSuggestion;
          if (!startFits(p2)) {
            setOpeningNote("that start is too long next to the ending you picked — unpick it first");
            return;
          }
          setPickedStart(p2);
        } else {
          const p2 = pick as ExtendSuggestion;
          if (!endFits(p2)) {
            setNote("that ending is too long next to the opening you picked — unpick it first");
            return;
          }
          setPicked(p2);
        }
      })
      .catch((e) => setNote_(friendlyError(e)))
      .finally(() => {
        if (direction === "start") setResolvingStart(false);
        else setResolvingEnd(false);
      });
  };

  /** Why a click landed outside the reachable zone, in the operator's terms. */
  const explainBlocked = (
    direction: "start" | "end",
    line: { startSec: number; endSec: number },
  ): void => {
    const secs =
      direction === "start"
        ? Math.round((ctx.sourceStartSec ?? 0) - line.startSec)
        : Math.round(line.endSec - (ctx.sourceEndSec ?? 0));
    const reach = Math.round(
      (direction === "start" ? (ctx.backHeadroomSec ?? 0) : (ctx.headroomSec ?? 0)),
    );
    const msg =
      direction === "start"
        ? `that's ~${secs}s before your clip, and only the last ~${reach}s fit under the ${ctx.maxClipSec}s cap — click a word closer to the clip.`
        : `that's ~${secs}s past your clip, and only the first ~${reach}s fit under the ${ctx.maxClipSec}s cap — click a word closer to the clip.`;
    if (direction === "start") setOpeningNote(msg);
    else setNote(msg);
  };

  const apply = (): void => {
    if ((picked === null && pickedStart === null) || applying || previewError !== null) return;
    setApplying(true);
    setApplyError(null);
    applyTrim(
      jobId,
      pickedStart === null ? 0 : -round1(pickedStart.addedSec),
      picked === null ? 0 : round1(picked.addedSec),
      undefined,
      {
        ...(picked !== null
          ? { extendEndPhrase: picked.endPhrase, extendEndsAtSourceSec: picked.endsAtSourceSec }
          : {}),
        ...(pickedStart !== null
          ? { extendStartPhrase: pickedStart.startPhrase, extendStartsAtSourceSec: pickedStart.startsAtSourceSec }
          : {}),
      },
    )
      .then(() => {
        // Same lifecycle as the panel's Apply: a following host (the Studio)
        // takes over; otherwise the finished job has no live SSE stream —
        // reload brings it back queued with progress visible.
        if (onJobStarted) {
          onJobStarted();
          onClose();
          return;
        }
        window.location.reload();
      })
      .catch((err) => {
        setApplyError(friendlyError(err));
        setApplying(false);
      });
  };

  const followOn = ctx.followOn ?? [];
  const leadIn = ctx.leadIn ?? [];
  const canExtendStart = (ctx.backHeadroomSec ?? 0) >= 2 && leadIn.length > 0;
  const addedTotal = (picked?.addedSec ?? 0) + (pickedStart?.addedSec ?? 0);
  // ONE budget feeds both edges (both grow the same span), and each direction's
  // suggestions were filtered against the WHOLE budget independently. So a pill
  // that fits on its own can still be unpickable next to an armed opposite pick
  // — say so on the pill instead of letting Apply fail with a cut-oriented 400.
  const budgetSec = ctx.headroomSec ?? 0;
  const endFits = (s2: ExtendSuggestion): boolean =>
    s2.addedSec + (pickedStart?.addedSec ?? 0) <= budgetSec;
  const startFits = (s2: OpeningSuggestion): boolean =>
    s2.addedSec + (picked?.addedSec ?? 0) <= budgetSec;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Smart extend"
      onClick={(e) => {
        if (e.target === e.currentTarget && !applying) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,8,14,.66)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 12 : 24,
        animation: "ql-fade-in .15s var(--ql-ease)",
      }}
    >
      <div
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "min(680px, calc(100vw - 48px))",
          maxHeight: "92vh",
          background: "rgba(16,18,27,.96)",
          border: "1px solid rgba(255,255,255,.10)",
          borderRadius: 18,
          boxShadow: "0 24px 60px rgba(0,0,0,.45)",
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          overflow: "hidden",
          animation: "ql-pop-in .18s var(--ql-ease)",
        }}
      >
        {/* Header — pinned */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: isMobile ? "12px 14px" : "16px 22px",
            borderBottom: "1px solid rgba(255,255,255,.08)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: isMobile ? 15 : 17,
                fontWeight: 700,
                color: "var(--ql-fg)",
                lineHeight: 1.35,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Sparkles size={16} strokeWidth={2} style={{ color: "var(--ql-yellow, #FFD500)" }} />
              Smart extend
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "var(--ql-fg-muted)",
                fontFamily: "var(--ql-font-mono)",
              }}
            >
              ~{Math.round(ctx.currentLenSec ?? 0)}s now · up to ~{Math.round(ctx.headroomSec ?? 0)}s more before
              the {ctx.maxClipSec}s cap
              {canExtendStart
                ? ` · shared across both ends (≤~${Math.round(ctx.backHeadroomSec ?? 0)}s earlier)`
                : ""}
              {ctx.windowSource !== "clip-window" ? " · timing approximate on this older render" : ""}
            </div>
          </div>
          <button
            onClick={() => {
              if (!applying) onClose();
            }}
            title="Close"
            aria-label="Close smart extend"
            autoFocus
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg-muted)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        {/* Body — the only scrolling region */}
        <div
          style={{
            overflowY: "auto",
            padding: isMobile ? 14 : 22,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {canExtendStart && (
            <section>
              <SectionLabel>
                What the speaker said before{" "}
                <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
                  — {resolvingStart ? "checking that spot…" : "click any word to start there"}
                </span>
              </SectionLabel>
              <ClickableTranscript
                lines={leadIn}
                direction="start"
                disabled={applying || resolvingStart}
                isEligible={(l) => l.endSec > (ctx.sourceStartSec ?? 0) - (ctx.backHeadroomSec ?? 0)}
                isAdded={(l) => pickedStart !== null && l.endSec > pickedStart.startsAtSourceSec}
                onPick={(phrase) => pickFromTranscript("start", phrase)}
                onBlocked={(line) => explainBlocked("start", line)}
              />
            </section>
          )}

          {canExtendStart && (
            <section>
              <SectionLabel>Suggested openings</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {openings?.map((s2) => {
                  const active =
                    pickedStart !== null && Math.abs(pickedStart.startsAtSourceSec - s2.startsAtSourceSec) < 0.05;
                  const fits = active || startFits(s2);
                  return (
                    <button
                      key={s2.startsAtSourceSec}
                      type="button"
                      onClick={() => {
                        if (!fits) return;
                        setPickedStart(active ? null : s2);
                      }}
                      disabled={applying || !fits}
                      aria-pressed={active}
                      style={pillStyle(active, applying || !fits)}
                    >
                      <span style={{ fontWeight: 700, fontFamily: "var(--ql-font-mono)", fontSize: 12 }}>
                        +{s2.addedSec.toFixed(1)}s earlier
                      </span>
                      <span style={{ color: "var(--ql-fg-muted)" }}> · starts on </span>
                      <span style={{ fontStyle: "italic" }}>
                        “{s2.startPhrase.split(/\s+/).slice(0, 6).join(" ")}…”
                      </span>
                      <div style={{ marginTop: 3, color: "var(--ql-fg-muted)", fontSize: 12 }}>
                        {fits ? s2.reason : "too long next to the ending you picked — unpick it first"}
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={suggestOpening}
                  disabled={openingBusy || applying}
                  style={{
                    ...pillStyle(false, openingBusy || applying),
                    textAlign: "center",
                    color: "var(--ql-yellow, #FFD500)",
                    fontWeight: 600,
                  }}
                >
                  {openingBusy ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Loader2 size={12} className="ql-spin" /> thinking…
                    </span>
                  ) : openings === null ? (
                    "Suggest openings"
                  ) : (
                    "Suggest again"
                  )}
                </button>
                <div aria-live="polite">
                  {openingNote !== null && (
                    <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.5 }}>
                      {openingNote}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          <section>
            <SectionLabel>
              What the speaker says next{" "}
              <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
                — {resolvingEnd ? "checking that spot…" : "click any word to end there"}
              </span>
            </SectionLabel>
            <ClickableTranscript
              lines={followOn}
              direction="end"
              disabled={applying || resolvingEnd}
              isEligible={(l) => l.startSec < (ctx.sourceEndSec ?? 0) + (ctx.headroomSec ?? 0)}
              isAdded={(l) => picked !== null && l.startSec < picked.endsAtSourceSec}
              onPick={(phrase) => pickFromTranscript("end", phrase)}
              onBlocked={(line) => explainBlocked("end", line)}
            />
          </section>

          <section>
            <SectionLabel>Suggested endings</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {suggestions?.map((s) => {
                const active = picked !== null && Math.abs(picked.endsAtSourceSec - s.endsAtSourceSec) < 0.05;
                const fits = active || endFits(s);
                return (
                  <button
                    key={s.endsAtSourceSec}
                    type="button"
                    onClick={() => {
                      if (!fits) return;
                      setPicked(active ? null : s);
                    }}
                    disabled={applying || !fits}
                    aria-pressed={active}
                    style={pillStyle(active, applying || !fits)}
                  >
                    <span style={{ fontWeight: 700, fontFamily: "var(--ql-font-mono)", fontSize: 12 }}>
                      +{s.addedSec.toFixed(1)}s → ~
                      {(s.newDurationSec + (pickedStart?.addedSec ?? 0)).toFixed(0)}s
                    </span>
                    <span style={{ color: "var(--ql-fg-muted)" }}> · ends after </span>
                    <span style={{ fontStyle: "italic" }}>
                      “…{s.endPhrase.split(/\s+/).slice(-6).join(" ")}”
                    </span>
                    <div style={{ marginTop: 3, color: "var(--ql-fg-muted)", fontSize: 12 }}>
                      {fits ? s.reason : "too long next to the opening you picked — unpick it first"}
                    </div>
                  </button>
                );
              })}
              {followOn.length > 0 && (
                <button
                  type="button"
                  onClick={suggest}
                  disabled={suggestBusy || applying}
                  style={{
                    ...pillStyle(false, suggestBusy || applying),
                    textAlign: "center",
                    color: "var(--ql-yellow, #FFD500)",
                    fontWeight: 600,
                  }}
                >
                  {suggestBusy ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Loader2 size={12} className="ql-spin" /> thinking…
                    </span>
                  ) : suggestions === null ? (
                    "Suggest endings"
                  ) : (
                    "Suggest again"
                  )}
                </button>
              )}
              <div aria-live="polite">
                {note !== null && (
                  <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.5 }}>{note}</div>
                )}
              </div>
            </div>
          </section>

          {/* Verified plan line for the picked ending. */}
          {(picked !== null || pickedStart !== null) && (
            <section>
              <div style={{ fontSize: 12, lineHeight: 1.55, minHeight: 16 }}>
                {previewPending ? (
                  <span
                    style={{
                      color: "var(--ql-fg-subtle)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Loader2 size={11} className="ql-spin" />
                    verifying the new bounds…
                  </span>
                ) : previewError ? (
                  <span style={{ color: "var(--ql-danger)" }}>{previewError}</span>
                ) : preview ? (
                  <span style={{ color: "var(--ql-fg)" }}>
                    New length ~{preview.newDurationEstSec.toFixed(0)}s
                    {pickedStart !== null && (
                      <>
                        {" — starts on "}
                        <em>
                          “{(preview.newFirstWords ?? pickedStart.startPhrase)
                            .split(/\s+/)
                            .slice(0, 6)
                            .join(" ")}…”
                        </em>
                      </>
                    )}
                    {picked !== null && (
                      <>
                        {pickedStart !== null ? "," : " —"} ends after{" "}
                        <em>
                          “…{(preview.newLastWords ?? picked.endPhrase).split(/\s+/).slice(-6).join(" ")}”
                        </em>
                      </>
                    )}
                  </span>
                ) : null}
              </div>
            </section>
          )}
        </div>

        {/* Footer — pinned */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            padding: isMobile ? "12px 14px" : "14px 22px",
            borderTop: "1px solid rgba(255,255,255,.08)",
          }}
        >
          {applyError !== null && (
            <span style={{ marginRight: "auto", fontSize: 12, color: "var(--ql-danger)" }}>{applyError}</span>
          )}
          <button
            type="button"
            onClick={() => {
              if (!applying) onClose();
            }}
            style={{
              padding: "10px 16px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.12)",
              background: "transparent",
              color: "var(--ql-fg-muted)",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={
              (picked === null && pickedStart === null) || applying || previewPending || previewError !== null
            }
            style={{
              ...ctaSmallStyle,
              opacity:
                (picked === null && pickedStart === null) || applying || previewPending || previewError !== null
                  ? 0.45
                  : 1,
              cursor:
                (picked === null && pickedStart === null) || applying || previewPending || previewError !== null
                  ? "default"
                  : "pointer",
            }}
          >
            {applying ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Loader2 size={13} className="ql-spin" /> re-cutting…
              </span>
            ) : picked !== null || pickedStart !== null ? (
              <>Extend +{addedTotal.toFixed(1)}s · re-render</>
            ) : canExtendStart ? (
              "Pick an opening or ending"
            ) : (
              "Pick an ending to extend"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
